
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Procurement } from '@/types/database.types';
import { calculateTotalAmount } from '@/utils/procurementCalculations';

interface CostBreakdownSectionProps {
  procurement: Procurement;
  onUpdateCosts: (costs: any) => Promise<void>;
  isLoading: boolean;
}

export const CostBreakdownSection: React.FC<CostBreakdownSectionProps> = ({
  procurement,
  onUpdateCosts,
  isLoading
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [costs, setCosts] = useState({
    price_per_unit: procurement.costs?.price_per_unit || 0,
    transport_cost_per_unit: procurement.costs?.transport_cost_per_unit || 0,
    loading_unloading_cost_per_unit: procurement.costs?.loading_unloading_cost_per_unit || 0,
    agent_cost_per_unit: procurement.costs?.agent_cost_per_unit || 0,
    other_cost_per_unit: procurement.costs?.other_cost_per_unit || 0,
    other_cost_description: procurement.costs?.other_cost_description || '',
    vat_applicable: procurement.costs?.vat_applicable || false
  });

  const handleSave = async () => {
    await onUpdateCosts(costs);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setCosts({
      price_per_unit: procurement.costs?.price_per_unit || 0,
      transport_cost_per_unit: procurement.costs?.transport_cost_per_unit || 0,
      loading_unloading_cost_per_unit: procurement.costs?.loading_unloading_cost_per_unit || 0,
      agent_cost_per_unit: procurement.costs?.agent_cost_per_unit || 0,
      other_cost_per_unit: procurement.costs?.other_cost_per_unit || 0,
      other_cost_description: procurement.costs?.other_cost_description || '',
      vat_applicable: procurement.costs?.vat_applicable || false
    });
    setIsEditing(false);
  };

  const totalCostPerUnit = costs.price_per_unit + costs.transport_cost_per_unit + 
    costs.loading_unloading_cost_per_unit + costs.agent_cost_per_unit + costs.other_cost_per_unit;
  
  const totalCost = totalCostPerUnit * (procurement.total_quantity || 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Cost Breakdown</CardTitle>
        {!isEditing && (
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            Edit Costs
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditing ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price_per_unit">Price per Unit (Br)</Label>
                <Input
                  id="price_per_unit"
                  type="number"
                  step="0.01"
                  value={costs.price_per_unit}
                  onChange={(e) => setCosts(prev => ({ ...prev, price_per_unit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="transport_cost">Transport Cost per Unit (Br)</Label>
                <Input
                  id="transport_cost"
                  type="number"
                  step="0.01"
                  value={costs.transport_cost_per_unit}
                  onChange={(e) => setCosts(prev => ({ ...prev, transport_cost_per_unit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="loading_cost">Loading/Unloading Cost per Unit (Br)</Label>
                <Input
                  id="loading_cost"
                  type="number"
                  step="0.01"
                  value={costs.loading_unloading_cost_per_unit}
                  onChange={(e) => setCosts(prev => ({ ...prev, loading_unloading_cost_per_unit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="agent_cost">Agent Cost per Unit (Br)</Label>
                <Input
                  id="agent_cost"
                  type="number"
                  step="0.01"
                  value={costs.agent_cost_per_unit}
                  onChange={(e) => setCosts(prev => ({ ...prev, agent_cost_per_unit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="other_cost">Other Cost per Unit (Br)</Label>
                <Input
                  id="other_cost"
                  type="number"
                  step="0.01"
                  value={costs.other_cost_per_unit}
                  onChange={(e) => setCosts(prev => ({ ...prev, other_cost_per_unit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="other_cost_description">Other Cost Description</Label>
                <Textarea
                  id="other_cost_description"
                  value={costs.other_cost_description}
                  onChange={(e) => setCosts(prev => ({ ...prev, other_cost_description: e.target.value }))}
                  placeholder="Describe other costs..."
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="vat_applicable"
                checked={costs.vat_applicable}
                onCheckedChange={(checked) => setCosts(prev => ({ ...prev, vat_applicable: checked }))}
              />
              <Label htmlFor="vat_applicable">VAT Applicable</Label>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-lg font-semibold">
                Total Cost: Br {totalCost.toFixed(2)} 
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  (Br {totalCostPerUnit.toFixed(2)} per unit × {procurement.total_quantity} kg)
                </span>
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="p-3 border rounded-md">
                <p className="text-sm font-medium">Price per Unit</p>
                <p className="text-sm">Br {procurement.costs?.price_per_unit?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="p-3 border rounded-md">
                <p className="text-sm font-medium">Transport Cost per Unit</p>
                <p className="text-sm">Br {procurement.costs?.transport_cost_per_unit?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="p-3 border rounded-md">
                <p className="text-sm font-medium">Loading/Unloading per Unit</p>
                <p className="text-sm">Br {procurement.costs?.loading_unloading_cost_per_unit?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="p-3 border rounded-md">
                <p className="text-sm font-medium">Agent Cost per Unit</p>
                <p className="text-sm">Br {procurement.costs?.agent_cost_per_unit?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="p-3 border rounded-md">
                <p className="text-sm font-medium">Other Cost per Unit</p>
                <p className="text-sm">Br {procurement.costs?.other_cost_per_unit?.toFixed(2) || '0.00'}</p>
              </div>
              
              <div className="p-3 border rounded-md">
                <p className="text-sm font-medium">VAT</p>
                <p className="text-sm">{procurement.costs?.vat_applicable ? 'Applicable' : 'Not applicable'}</p>
              </div>
            </div>
            
            {procurement.costs?.other_cost_description && (
              <div className="p-3 border rounded-md">
                <p className="text-sm font-medium">Other Cost Description</p>
                <p className="text-sm">{procurement.costs.other_cost_description}</p>
              </div>
            )}
            
            <div className="border-t pt-4">
              <p className="text-lg font-semibold">
                Total Cost: Br {calculateTotalAmount(procurement).toFixed(2)}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
